import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Loader2, ScrollText } from 'lucide-react';
import { api, ApiError } from '../api/client.js';
import { Card } from '../components/Card.jsx';
import { Banner } from '../components/Banner.jsx';
import { Gauge, GAUGE_ERR_PCT, GAUGE_WARN_PCT } from '../components/Gauge.jsx';

/**
 * §13 Phase B — the dashboard.
 *
 * THE RULE THIS PAGE IS BUILT AROUND (SPEC-13 §6): no placeholder numbers,
 * ever. If `GET /api/host/stats` did not return a field, it does not appear —
 * not as 0, not as "—", not as "N/A". A dashboard is what you glance at instead
 * of checking, so a wrong number on it is trusted precisely when nobody is
 * looking closely. Every `stats.x != null` guard below is that rule, not
 * defensive habit.
 *
 * Copy that is NOT measured — the plan name and the two spend figures — comes
 * from `server/contabo-167.86.109.176.md` §1 and is marked as such below.
 * Everything else on the page is measured at request time.
 */

/**
 * The only hardcoded facts on this page. Source: hub doc
 * `server/contabo-167.86.109.176.md` §1 — "Provider / plan: Contabo VPS 30",
 * "Cost: ~€11/mo (replaced ~€87/mo of VPS1+VPS2+VPS3)". Not retyped from the
 * mockup: the mockup's "200 GB NVMe" disagrees with the doc's 193 GB, so the
 * hardware line is rendered from what the endpoint measured instead, and only
 * the plan name (unmeasurable) is copy.
 *
 * The backup box (135.181.244.40) is deliberately NOT in the spend figure —
 * SPEC-13's hero is the single machine, and mixing the two would make €11 mean
 * two different things.
 */
const HOST_PLAN = 'Contabo VPS 30';
const SPEND_NOW = '€11';
const SPEND_BEFORE = '€87';

export function Dashboard() {
  const [summary, setSummary] = useState({ status: 'loading' });
  const [host, setHost] = useState({ status: 'loading' });
  const [domains, setDomains] = useState({ status: 'loading' });

  useEffect(() => {
    api('/api/dashboard/summary')
      .then((data) => setSummary({ status: 'ok', data }))
      .catch((err) => setSummary({ status: 'error', message: err.message }));

    // Domains about to expire (DNS feature). 403 = no DNS access → no line at all.
    api('/api/dns/domains')
      .then((data) => setDomains({ status: 'ok', data }))
      .catch(() => setDomains({ status: 'hidden' }));

    api('/api/host/stats')
      .then((data) => setHost({ status: 'ok', data }))
      .catch((err) => {
        // 403 is not an error to report: /api/host/stats is manager-only, so a
        // member simply has no box strip. Anything else is worth saying.
        if (err instanceof ApiError && err.status === 403) {
          setHost({ status: 'forbidden' });
        } else {
          setHost({ status: 'error', message: err.message });
        }
      });
  }, []);

  return (
    <div>
      <BoxStrip state={host} />
      <MetricRow summary={summary} />
      <StaticVisitsRow summary={summary} />
      <ExpiringDomains state={domains} />

      <h3 className="mt-6 mb-3 text-[11px] font-semibold uppercase tracking-[0.09em] text-t3">
        Recent activity
      </h3>
      <Card>
        <RecentActivity summary={summary} />
      </Card>
    </div>
  );
}

// ─── Domains expiring soon ──────────────────────────────────────────────────

/** Only when there is something to act on; an unreadable account is said, never counted as 0. */
function ExpiringDomains({ state }) {
  if (state.status !== 'ok') return null;
  const { domains, accounts } = state.data;
  const soon = domains.filter((d) => d.days_left !== null && d.days_left < 30).sort((a, b) => a.days_left - b.days_left);
  const unread = accounts.filter((a) => !a.ok);
  if (!soon.length && !unread.length) return null;
  return (
    <Banner
      kind={soon.some((d) => d.days_left < 7) ? 'error' : 'note'}
      title={soon.length ? `${soon.length} domain${soon.length === 1 ? '' : 's'} expire${soon.length === 1 ? 's' : ''} in < 30 days` : 'Domains: an account could not be read'}
      action={<Link to="/dns" className="text-[13px] font-medium text-blue hover:underline">DNS manager</Link>}
      className="mt-4"
    >
      {soon.map((d) => `${d.name} — ${d.days_left < 0 ? 'expired' : `${d.days_left} days`}${d.auto_renew ? ' (auto-renew on)' : ''}`).join(' · ')}
      {unread.length > 0 && <span className="block">Not read: {unread.map((a) => a.label).join(', ')} — their domains are not counted.</span>}
    </Banner>
  );
}

// ─── The box ────────────────────────────────────────────────────────────────

/**
 * Health of the whole box, from real state only.
 *
 * `unknown` is a first-class outcome: SPEC-13 §6 makes the left accent bar a
 * status indicator, and PROMPT-13 is explicit that anything we cannot determine
 * is NOT healthy-by-default. So a missing metric or an unclassified service
 * state lands on `warn`, never on the teal healthy bar.
 */
function boxHealth(stats) {
  const reasons = [];
  let level = 'ok';
  const raise = (next) => {
    if (next === 'err' || (next === 'warn' && level === 'ok')) level = next;
  };

  const gauges = [
    ['CPU', stats.cpu?.pct],
    ['RAM', stats.memory?.pct],
    ['Disk', stats.disk?.pct],
  ];
  for (const [label, pct] of gauges) {
    if (pct == null) continue;
    if (pct >= GAUGE_ERR_PCT) { raise('err'); reasons.push(`${label} at ${Math.round(pct)}%`); }
    else if (pct >= GAUGE_WARN_PCT) { raise('warn'); reasons.push(`${label} at ${Math.round(pct)}%`); }
  }

  const down = (stats.services ?? []).filter((s) => s.up === false);
  if (down.length) {
    raise('err');
    reasons.push(
      // "6 WordPress — 1 not running" reads correctly for the rolled-up docker
      // group as well as for a single pm2 process ("tapi — stopped").
      down.length === 1
        ? `${down[0].name} — ${down[0].status ?? 'not running'}`
        : `${down.length} services not running`,
    );
  }
  const murky = (stats.services ?? []).filter((s) => s.up === null);
  if (murky.length) { raise('warn'); reasons.push(`${murky.length} in an unclassified state`); }

  const missing = Object.keys(stats.unavailable ?? {});
  if (missing.length) { raise('warn'); reasons.push(`not measured: ${missing.join(', ')}`); }

  return { level, reasons };
}

const ACCENT = { ok: 'border-l-teal', warn: 'border-l-warn', err: 'border-l-err' };
const STATE_TEXT = { ok: 'text-ok', warn: 'text-warn', err: 'text-err' };
const STATE_DOT = { ok: 'bg-ok', warn: 'bg-warn', err: 'bg-err' };

function BoxStrip({ state }) {
  // Manager-only endpoint: a member gets no strip at all rather than an error.
  if (state.status === 'forbidden') return null;

  if (state.status === 'loading') {
    return (
      <StripShell level="ok">
        <div className="flex items-center gap-2 text-[13px] text-t3">
          <Loader2 className="w-4 h-4 animate-spin" /> Reading host metrics…
        </div>
      </StripShell>
    );
  }

  if (state.status === 'error') {
    return (
      <Banner
        kind="error"
        title="Host metrics unavailable"
        className="mb-5"
      >
        {state.message}. Nothing about the box is being reported below — the
        figures are absent, not zero.
      </Banner>
    );
  }

  const stats = state.data;
  const { level, reasons } = boxHealth(stats);

  return (
    <StripShell level={level}>
      <div className="grid gap-8 wide:grid-cols-[minmax(230px,1fr)_minmax(300px,1.2fr)_minmax(230px,1fr)]">
        <div>
          <p className="mb-[9px] text-[11px] font-semibold uppercase tracking-[0.09em] text-t3">
            The box
          </p>
          {stats.host?.ipv4 && (
            <div className="font-mono text-[19px] font-medium tracking-[-0.02em] text-t1">
              {stats.host.ipv4}
            </div>
          )}
          <p className="mt-[7px] text-[12.5px] leading-[1.65] text-t2">
            {[HOST_PLAN, stats.host?.os].filter(Boolean).join(' · ')}
            <br />
            <b className="font-medium">{hardwareLine(stats)}</b>
          </p>
          {stats.host?.uptime_seconds != null && (
            <span
              className={`mt-[11px] inline-flex items-center gap-1.5 text-[12px] font-medium ${STATE_TEXT[level]}`}
            >
              <i className={`block w-[7px] h-[7px] rounded-full ${STATE_DOT[level]}`} aria-hidden="true" />
              Up {formatUptime(stats.host.uptime_seconds)}
            </span>
          )}
          {reasons.length > 0 && (
            <p className={`mt-1.5 text-[12px] ${STATE_TEXT[level]}`}>{reasons.join(' · ')}</p>
          )}
        </div>

        <BoxGauges stats={stats} />
        <BoxServices services={stats.services} />
      </div>
    </StripShell>
  );
}

/**
 * Square by design — §5's "no rounded corners on single-sided borders". The 4px
 * left bar is the status indicator, so it must not be paired with a radius.
 */
function StripShell({ level, children }) {
  return (
    <section
      className={`mb-5 border border-bd border-l-4 ${ACCENT[level]} bg-white px-6 py-5`}
      aria-label="Host status"
    >
      {children}
    </section>
  );
}

/** Only gauges whose metric the endpoint actually returned. */
function BoxGauges({ stats }) {
  const rows = [];
  if (stats.cpu?.pct != null) {
    rows.push({
      key: 'cpu',
      label: 'CPU',
      pct: stats.cpu.pct,
      value: `${Math.round(stats.cpu.pct)}%`,
      // Load average is a busy-ness proxy over a minute, not instantaneous
      // utilisation. Say so on hover rather than let "14%" imply more precision
      // than the number has.
      title: stats.cpu.load
        ? `1-minute load ${stats.cpu.load[0]} across ${stats.cpu.cores} vCPU`
        : undefined,
    });
  }
  if (stats.memory?.pct != null) {
    rows.push({
      key: 'ram',
      label: 'RAM',
      pct: stats.memory.pct,
      value: formatBytes(stats.memory.used_bytes),
      title: `${formatBytes(stats.memory.used_bytes)} used of ${formatBytes(stats.memory.total_bytes)}`,
    });
  }
  if (stats.disk?.pct != null) {
    rows.push({
      key: 'disk',
      label: 'Disk',
      pct: stats.disk.pct,
      value: formatBytes(stats.disk.used_bytes),
      title: `${formatBytes(stats.disk.used_bytes)} used of ${formatBytes(stats.disk.size_bytes)} on ${stats.disk.mount}`,
    });
  }

  if (rows.length === 0) {
    return (
      <p className="self-center text-[12px] text-t3">
        CPU, RAM and disk were not measured on this host.
      </p>
    );
  }

  return (
    <div className="flex flex-col justify-center gap-[13px]">
      {rows.map((r) => (
        <div key={r.key} title={r.title}>
          <Gauge label={r.label} pct={r.pct} value={r.value} />
        </div>
      ))}
    </div>
  );
}

/**
 * pm2 and docker are enumerated in full; systemd is a named list (see
 * SYSTEMD_UNITS in backend/lib/hostStats.js).
 *
 * The heading says "Tracked here", not "Running here", and the footnote spells
 * out the coverage. That is deliberate: the systemd side cannot discover a new
 * unit by itself, so a heading promising everything on the box would claim more
 * than the data supports — and the whole point of this strip is that you can
 * trust it at a glance.
 */
function BoxServices({ services }) {
  const systemdCount = (services ?? []).filter((s) => s.source === 'systemd').length;

  return (
    <div>
      <p className="mb-[9px] text-[11px] font-semibold uppercase tracking-[0.09em] text-t3">
        Tracked here
      </p>
      {services == null ? (
        <p className="text-[12px] text-t3">
          None of systemd, pm2 or docker could be read — the service list is
          unknown, not empty.
        </p>
      ) : services.length === 0 ? (
        <p className="text-[12px] text-t3">No services reported.</p>
      ) : (
        <>
          <div className="flex flex-wrap content-start gap-1.5">
            {services.map((s) => (
              <ServiceChip key={`${s.source}:${s.name}`} service={s} />
            ))}
          </div>
          <p className="mt-2 text-[11px] leading-snug text-t3">
            Every pm2 process and docker container
            {systemdCount > 0 && `, plus ${systemdCount} named systemd units`}.
          </p>
        </>
      )}
    </div>
  );
}

function ServiceChip({ service }) {
  const down = service.up === false;
  const unknown = service.up == null;
  return (
    <span
      title={`${service.source}${service.status ? ` · ${service.status}` : ''}`}
      className={[
        'inline-flex items-center gap-1.5 rounded-ctl border px-2 py-1 font-mono text-[11.5px]',
        down
          ? 'border-err-bd bg-err-bg text-err'
          : unknown
          ? 'border-bd bg-s-3 text-t3'
          : 'border-bd bg-s-3 text-t2',
      ].join(' ')}
    >
      <i
        className={[
          'block w-[7px] h-[7px] shrink-0 rounded-full',
          down ? 'bg-err' : unknown ? 'bg-ic-light' : 'bg-ok',
        ].join(' ')}
        aria-hidden="true"
      />
      {service.name}
      {/* Status is never colour alone (§8). A stopped service says so in words —
          and so does one whose state we could not determine, because a silent
          grey chip is indistinguishable from a healthy one at a glance. */}
      {(down || unknown) && (
        <span className="not-italic">· {service.status ?? (down ? 'not running' : 'unknown')}</span>
      )}
    </span>
  );
}

function hardwareLine(stats) {
  const parts = [];
  if (stats.cpu?.cores) parts.push(`${stats.cpu.cores} vCPU`);
  if (stats.memory?.total_bytes) parts.push(formatBytes(stats.memory.total_bytes));
  if (stats.disk?.size_bytes) parts.push(formatBytes(stats.disk.size_bytes));
  return parts.join(' · ');
}

// ─── Metric cards ───────────────────────────────────────────────────────────

function MetricRow({ summary }) {
  if (summary.status === 'error') {
    return (
      <div className="mb-5 flex items-start gap-3 border border-err-bd bg-err-bg p-4 text-err">
        <AlertTriangle className="mt-0.5 w-5 h-5 shrink-0" />
        <div>
          <p className="text-[13.5px] font-semibold">Failed to load dashboard summary</p>
          <p className="mt-0.5 text-[12.5px]">{summary.message}</p>
        </div>
      </div>
    );
  }

  const loading = summary.status === 'loading';
  const data = summary.status === 'ok' ? summary.data : null;

  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <SitesCard loading={loading} sites={data?.sites} />
      <BackupsCard
        loading={loading}
        stats={data?.backups_last_24h}
        scheduled={data?.scheduled_backups}
      />
      <SpendCard />
    </div>
  );
}

// ─── Static sites: real visitors (hourly, from each site's access log) ─────

function StaticVisitsRow({ summary }) {
  const rows = summary.status === 'ok' ? summary.data.static_visits ?? [] : [];
  if (!rows.length) return null;
  const n = (v) => (v == null ? '—' : v); // never stored = unknown, not 0
  return (
    <div className="mt-4 grid gap-4 sm:grid-cols-3">
      {rows.map((v) => {
        const failed = v.last_refresh && !v.last_refresh.ok;
        return (
          <MetricCard key={v.domain} to="/sites" eyebrow={`Visitors · ${v.domain}`}>
            <Big>{n(v.today)}</Big>
            <Sub>
              today · <span className="font-medium text-t1">{n(v.last7)}</span> in 7 days
              {!v.marker && ' · counted from / (less exact)'}
              {failed && <span className="block text-err" title={v.last_refresh.detail}>last count failed: {v.last_refresh.error}</span>}
            </Sub>
          </MetricCard>
        );
      })}
    </div>
  );
}

function MetricCard({ to, eyebrow, children }) {
  const body = (
    <>
      <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.09em] text-t3">
        {eyebrow}
      </p>
      {children}
    </>
  );
  const className = 'block bg-white border border-bd rounded-card px-[18px] py-4';
  return to ? (
    <Link to={to} className={`${className} hover:border-bd-strong`}>{body}</Link>
  ) : (
    <div className={className}>{body}</div>
  );
}

function Big({ children, tone = 'text-t1' }) {
  return <div className={`text-[26px] font-semibold leading-none ${tone}`}>{children}</div>;
}

function Sub({ children }) {
  return <p className="mt-2 text-[12.5px] text-t2">{children}</p>;
}

function SitesCard({ loading, sites }) {
  const error = sites?.error;
  return (
    <MetricCard to="/sites" eyebrow="Sites">
      {loading ? (
        <Big tone="text-t3"><Loader2 className="inline w-5 h-5 animate-spin" /></Big>
      ) : error ? (
        // No count is known, so none is shown — not a zero.
        <p className="text-[13.5px] font-medium text-warn">Count unavailable</p>
      ) : (
        <Big>{sites?.count}</Big>
      )}
      <Sub>
        {loading && 'Loading…'}
        {error === 'vps1_api_not_configured' && 'vps1-api is not configured'}
        {error === 'vps1_api_error' &&
          `vps1-api unreachable${sites.upstream_status ? ` (HTTP ${sites.upstream_status})` : ''}`}
        {!loading && !error && 'WordPress sites in the registry'}
      </Sub>
    </MetricCard>
  );
}

function BackupsCard({ loading, stats, scheduled }) {
  if (loading) {
    return (
      <MetricCard to="/audit" eyebrow="Backups">
        <Big tone="text-t3"><Loader2 className="inline w-5 h-5 animate-spin" /></Big>
        <Sub>Loading…</Sub>
      </MetricCard>
    );
  }

  const failed = stats?.failed ?? 0;
  const succeeded = stats?.succeeded ?? 0;

  let headline;
  if (failed > 0) headline = <Big tone="text-err">{failed} failed</Big>;
  else if (succeeded > 0) headline = <Big tone="text-ok">Green</Big>;
  else if (!scheduled?.enabled) headline = <Big tone="text-t3">Off</Big>;
  else headline = <Big tone="text-t3">None</Big>;

  let sub;
  if (!scheduled?.enabled) sub = 'Scheduled backups are disabled';
  else if (scheduled.last_run_at) {
    sub = `${succeeded + failed} in 24h · last scheduled run ${formatRelative(scheduled.last_run_at)}`;
  } else sub = `Cron "${scheduled.cron}" — no runs recorded yet`;

  return (
    <MetricCard to="/audit" eyebrow="Backups">
      {headline}
      <Sub>{sub}</Sub>
    </MetricCard>
  );
}

/**
 * The one card with no live data behind it, and the reason the dashboard exists:
 * three VPS at ~€87/mo became one at ~€11/mo. Figures from the hub's server doc
 * (see HOST_PLAN above), not from the panel — nothing here reads a bill.
 */
function SpendCard() {
  return (
    <MetricCard eyebrow="Monthly spend">
      <Big>{SPEND_NOW}</Big>
      <Sub>
        was <span className="text-t3 line-through">{SPEND_BEFORE}</span> on three VPS
      </Sub>
    </MetricCard>
  );
}

// ─── Recent activity ────────────────────────────────────────────────────────

function RecentActivity({ summary }) {
  if (summary.status === 'loading') {
    return (
      <div className="flex items-center justify-center p-12 text-[13.5px] text-t3">
        <Loader2 className="mr-2 w-4 h-4 animate-spin" /> Loading events…
      </div>
    );
  }
  if (summary.status === 'error') {
    return <div className="p-6 text-[13.5px] text-err">Couldn't load activity: {summary.message}</div>;
  }
  const events = summary.data?.recent_events ?? [];
  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center p-12 text-center">
        <ScrollText className="mb-3 w-10 h-10 text-ic-light" />
        <p className="text-[13.5px] font-medium text-t2">No events yet</p>
        <p className="mt-1 text-[12.5px] text-t3">
          Auth attempts, site actions and cron runs show up here.
        </p>
      </div>
    );
  }
  return (
    <div>
      {events.map((ev) => (
        <ActivityRow key={ev.id} event={ev} />
      ))}
      <div className="border-t border-bd px-5 py-2.5 text-right">
        <Link to="/audit" className="text-[12.5px] text-t3 hover:text-t1">
          View all →
        </Link>
      </div>
    </div>
  );
}

const BAD_EVENT = /(failed|rejected|denied)/;

function ActivityRow({ event }) {
  const bad = BAD_EVENT.test(event.event_type);
  return (
    <div className="flex items-baseline gap-3.5 border-b border-bd px-5 py-2.5 last:border-b-0">
      <time
        className="shrink-0 font-mono text-[12px] text-t3"
        title={formatAbsolute(event.created_at)}
      >
        {formatRelative(event.created_at)}
      </time>
      <p className="min-w-0 text-[13.5px] text-t2">
        <b className="font-semibold text-t1">{actorOf(event)}</b>{' '}
        <span className={bad ? 'text-err' : undefined}>{event.event_type}</span>
        {event.target && (
          <>
            {' '}
            <code className="font-mono text-[12px] text-t2">{event.target}</code>
          </>
        )}
        {event.metadata?.trigger === 'scheduled' && (
          <span className="text-t3"> · scheduled</span>
        )}
      </p>
    </div>
  );
}

/** Local part of the email, or "system" for events with no actor. */
function actorOf(event) {
  if (!event.email) return 'system';
  return event.email.split('@')[0];
}

// ─── Formatting ─────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return null;
  const gb = bytes / 1024 ** 3;
  // One decimal only where it carries information: "9.8 GB" is worth saying,
  // "24.0 GB" just looks like a machine wrote it.
  if (gb >= 10) return `${Math.round(gb)} GB`;
  if (gb >= 1) return `${gb.toFixed(1)} GB`;
  return `${Math.round(bytes / 1024 ** 2)} MB`;
}

function formatUptime(seconds) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${plural(d, 'day')}, ${plural(h, 'hour')}`;
  if (h > 0) return `${plural(h, 'hour')}, ${plural(m, 'minute')}`;
  return plural(m, 'minute');
}

function plural(n, word) {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

// SQLite CURRENT_TIMESTAMP returns "YYYY-MM-DD HH:MM:SS" in UTC (no TZ suffix).
// Normalize to ISO with explicit 'Z' so Date() parses as UTC, not local time.
function parseSqliteTimestamp(value) {
  if (!value) return null;
  if (value.includes('T')) return new Date(value);
  return new Date(value.replace(' ', 'T') + 'Z');
}

function formatRelative(value) {
  const date = parseSqliteTimestamp(value);
  if (!date) return '—';
  const diffSec = Math.round((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return rtf.format(-diffSec, 'second');
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return rtf.format(-diffMin, 'minute');
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return rtf.format(-diffHr, 'hour');
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return rtf.format(-diffDay, 'day');
  return date.toLocaleDateString();
}

function formatAbsolute(value) {
  const date = parseSqliteTimestamp(value);
  if (!date) return '';
  return date.toLocaleString();
}
